import { SubscriptionUpdateType } from "@coeff/api";
import { message } from "antd";
import moment from "moment";
import React, { useCallback, useContext, useState } from "react";
import styled from "styled-components";

import { Button, FlexColumn, Modal } from "../../../components";
import { COLORS } from "../../../constants";
import { BContext } from "../../../types";
import { useTrack } from "../../../utils";
import { BillingContext } from "../Billing";

type Props = {
  visible: boolean;
  onClose: () => void;
};

type UpdateSubResponse = { is_success?: boolean; error_msg?: string };

const BodyBlock = styled.div`
  color: ${COLORS.black65};
  font-size: 14px;
  line-height: 1.6;
`;

const FooterRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  padding-top: 8px;
`;

/** Self-serve removal flow for the AI Dashboards addon. Takes effect at the end of the term. */
export const CancelWebDashboardModal: React.FC<Props> = ({ visible, onClose }) => {
  const { userSubscription, updateSubscription, syncBilling } = useContext(
    BillingContext
  ) as BContext;

  const track = useTrack();

  const [submitting, setSubmitting] = useState(false);

  const subscriptionDetails = userSubscription?.subscription_details;

  const periodEndOnDate = subscriptionDetails?.next_billing_cycle_dt ? (
    <>
      {" "}
      on{" "}
      <strong style={{ color: COLORS.black85 }}>
        {moment(subscriptionDetails.next_billing_cycle_dt).format("MMM DD, YYYY")}
      </strong>
    </>
  ) : null;

  const handleClose = useCallback(() => {
    if (!submitting) {
      onClose();
    }
  }, [onClose, submitting]);

  const handleConfirm = useCallback(async () => {
    if (!subscriptionDetails?.subscription_id) {
      message.error("Missing subscription. Please refresh and try again.");
      return;
    }

    track("billing_ai_dashboards_remove_clicked", {
      current_plan_type: subscriptionDetails?.plan_type,
    });

    setSubmitting(true);
    try {
      const res = (await updateSubscription({
        update_type: SubscriptionUpdateType.RemoveWebDashboardAddon,
        subscription_id: subscriptionDetails.subscription_id,
      })) as UpdateSubResponse;

      if (res?.is_success) {
        track("billing_ai_dashboards_remove_completed", {
          current_plan_type: subscriptionDetails?.plan_type,
        });
        await syncBilling();
        onClose();
        message.success("AI Dashboards will be removed at the end of your billing period.");
      } else {
        message.error(res?.error_msg ?? "Something went wrong.");
      }
    } catch (e: unknown) {
      message.error(e instanceof Error ? e.message : "Request failed.");
    } finally {
      setSubmitting(false);
    }
  }, [subscriptionDetails, updateSubscription, syncBilling, onClose, track]);

  if (!subscriptionDetails) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      onCancel={handleClose}
      title="Remove AI Dashboards?"
      width={480}
      centered={true}
    >
      <FlexColumn gap={0}>
        <BodyBlock>
          <p style={{ margin: 0 }}>
            AI Dashboards stays active until the end of your current billing period
            {periodEndOnDate}. After that, your dashboards will be archived — nothing is deleted,
            and you can restore them anytime by re-adding AI Dashboards.
          </p>
        </BodyBlock>

        <FooterRow>
          <Button
            type="link"
            noPadding
            onClick={handleClose}
            disabled={submitting}
            style={{ fontWeight: 600 }}
          >
            Keep add-on
          </Button>
          <Button type="primary" loading={submitting} onClick={handleConfirm}>
            Remove add-on
          </Button>
        </FooterRow>
      </FlexColumn>
    </Modal>
  );
};
